import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * «Deshacer» de 5 segundos del panel (G159, ADR de P0-10): la petición **no sale** hasta que pasan los 5 s; «Deshacer»
 * simplemente no la envía. Nunca para lo que avisa al cliente, mueve dinero, fusiona, suprime o importa (van sin
 * «Deshacer» o con ventana de confirmación). Solo hay un envío pendiente a la vez: si llega otro, el anterior sale ya;
 * y si la pantalla se cierra antes de tiempo, sale también (la persona no lo deshizo).
 *
 * La pieza visual es `componentes/Deshacer.tsx`; el tiempo lo lleva este gancho, no el botón (que puede no estar a la
 * vista, p. ej. al cerrar la ficha).
 */
export const ESPERA_DESHACER_MS = 5000;

export interface EnvioRetrasado {
    /** Cambia con cada envío (reinicia la línea del botón). */
    clave: number;
    /** Lo que dice el aviso («Inés Calvo ya está en tus clientes.»). */
    texto: string;
    /** Dónde se pinta el aviso (la pantalla decide). */
    donde: string;
}

interface Pendiente extends EnvioRetrasado {
    enviar: () => void;
    alDeshacer?: () => void;
}

export function useEnvioRetrasado(espera = ESPERA_DESHACER_MS) {
    const [visible, setVisible] = useState<EnvioRetrasado | null>(null);
    const pendiente = useRef<Pendiente | null>(null);
    const reloj = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const contador = useRef(0);

    const soltar = useCallback((): Pendiente | null => {
        clearTimeout(reloj.current);
        const p = pendiente.current;
        pendiente.current = null;
        setVisible(null);
        return p;
    }, []);

    /** Programa un envío: sale a los 5 s salvo «Deshacer». */
    const programar = useCallback(
        (envio: { texto: string; donde: string; enviar: () => void; alDeshacer?: () => void }) => {
            soltar()?.enviar();
            contador.current += 1;
            const nuevo: Pendiente = { ...envio, clave: contador.current };
            pendiente.current = nuevo;
            setVisible({ clave: nuevo.clave, texto: nuevo.texto, donde: nuevo.donde });
            reloj.current = setTimeout(() => {
                soltar()?.enviar();
            }, espera);
        },
        [espera, soltar],
    );

    /** «Deshacer»: no se envía. */
    const deshacer = useCallback(() => {
        soltar()?.alDeshacer?.();
    }, [soltar]);

    // Si la pantalla se va con algo pendiente, sale ya.
    useEffect(
        () => () => {
            clearTimeout(reloj.current);
            const p = pendiente.current;
            pendiente.current = null;
            p?.enviar();
        },
        [],
    );

    return { pendiente: visible, programar, deshacer, duracion: espera };
}
